import React from 'react'
import { StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { EVENT_LIMITS, type EventFormState } from '@utils'
import { useScreen } from '@hooks'

export interface VenueFieldProps {
  values: Pick<EventFormState, 'location' | 'address' | 'city' | 'latitude' | 'longitude'>
  /** Translated "venue required" message. */
  error?: string
  onChange: <K extends 'location' | 'address' | 'city' | 'latitude' | 'longitude'>(
    key: K,
    value: EventFormState[K],
  ) => void
}

/** Venue typed by hand: name, address, city. Typing drops any picked coordinates. */
export function VenueManualFields({ values, error, onChange }: VenueFieldProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isMobile } = useScreen()
  const styles = getStyles(theme)

  const edit = (key: 'location' | 'address' | 'city') => (value: string) => {
    onChange(key, value)
    onChange('latitude', null)
    onChange('longitude', null)
  }

  return (
    <>
      <TextInput
        title={t('events.form.location')}
        placeholder={t('events.form.locationPlaceholder')}
        value={values.location}
        onChangeText={edit('location')}
        maxLength={EVENT_LIMITS.locationMax}
        iconRight="mapPin"
        errorMessage={error}
        testID="event-location-input"
      />
      <View style={[styles.row, isMobile && styles.column]}>
        <View style={isMobile ? undefined : styles.wide}>
          <TextInput
            title={t('events.form.address')}
            placeholder={t('events.form.addressPlaceholder')}
            value={values.address}
            onChangeText={edit('address')}
            maxLength={EVENT_LIMITS.addressMax}
            testID="event-address-input"
          />
        </View>
        <View style={isMobile ? undefined : styles.narrow}>
          <TextInput
            title={t('events.form.city')}
            placeholder={t('events.form.cityPlaceholder')}
            value={values.city}
            onChangeText={edit('city')}
            maxLength={EVENT_LIMITS.cityMax}
            testID="event-city-input"
          />
        </View>
      </View>
    </>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    row: { flexDirection: 'row', gap: theme.spacing.lg },
    column: { flexDirection: 'column' },
    wide: { flex: 2 },
    narrow: { flex: 1 },
  })
